// src/layout/layout.tsx
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { getCurrentUser, logoutUser, refreshToken } from '../api/auth/auth';
import { subscribe, getRemainingSeconds, setRemainingSeconds } from '../api/auth/sessionTTL';

import '../assets/styles/layout.scss';

export default function Layout() {
  const [email, setEmail] = useState<string | null>(null);
  const [ttl, setTtl] = useState<number | null>(getRemainingSeconds());
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const inAdmin = location.pathname.startsWith('/admin');
  const isWelcome = location.pathname === '/admin/welcome';
  const showTopNav = !!email && !isWelcome;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const user = await getCurrentUser();
        if (active) setEmail(user?.email || null);
      } catch {
        if (active) setEmail(null);
      }
    })();

    // sub na TTL z interceptora odpowiedzi
    const unsub = subscribe((sec) => {
      if (!active) return;
      setTtl(sec);
    });

    // lokalny licznik, żeby UI schodził co sekundę bez pingowania API
    const tick = setInterval(() => {
      setTtl((prev) => (typeof prev === 'number' ? Math.max(0, prev - 1) : prev));
    }, 1000);

    // zamknij mobilne menu po każdej zmianie trasy
    setMenuOpen(false);

    return () => {
      active = false;
      clearInterval(tick);
      unsub();
    };
  }, [location.pathname]);

  // auto-logout gdy TTL spadnie do 0
  useEffect(() => {
    if (typeof ttl === 'number' && ttl === 0) {
      (async () => {
        await handleLogout();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttl]);

  const handleLogout = async () => {
    await logoutUser();
    setEmail(null);
    navigate('/');
  };

  const handleRefresh = async () => {
    try {
      const newTok = await refreshToken();
      // pozwól interceptorowi z nagłówków ustawić świeży TTL
      setRemainingSeconds(null);
    } catch {
      // refresh nieudany → wyloguj
      await handleLogout();
    }
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const closeMenu = () => setMenuOpen(false);

  // Linki z lewego admin-sidebaru przeniesione do burgera (mobile)
  const adminLinks = useMemo(
    () => [
      { to: '/admin', label: 'Dashboard' },
      { to: '/admin/contents', label: 'Treści' },
      { to: '/admin/series', label: 'Series' },
      { to: '/admin/categories', label: 'Kategorie' },
      { to: '/admin/tags', label: 'Tagi' },
      { to: '/admin/authors', label: 'Autorzy' },
      { to: '/admin/media', label: 'Media' },
      { to: '/admin/generate-audio', label: 'Generator audio' },
      { to: '/admin/promocodes', label: 'Kody rabatowe' },
      { to: '/admin/rate', label: 'Oceny' },
      { to: '/admin/notifications', label: 'Powiadomienia' },
      { to: '/admin/users', label: 'Użytkownicy' },
      { to: '/admin/errors', label: 'Błędy' },
      { to: '/admin/rules', label: 'Regulamin' },
    ],
    []
  );

  const renderTtl = () => {
    if (typeof ttl !== 'number') return null;
    const minutes = Math.floor(ttl / 60);
    const seconds = String(ttl % 60).padStart(2, '0');
    const warn = ttl <= 600; // 10 min
    return (
      <div className="session-ttl" title="Pozostały czas sesji">
        <span className={`ttl-chip ${warn ? 'warn' : ''}`}>
          {minutes}:{seconds}
        </span>
        {warn && (
          <button className="btn-ghost" onClick={handleRefresh}>
            Przedłuż sesję
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {showTopNav && (
        <>
          <nav className="top-nav" aria-label="Główna nawigacja">
            <div className="nav-inner">
              <div className="brand">
                <Link
                  to="/"
                  className="brand-link"
                  aria-label="Strona główna"
                  onClick={closeMenu}
                >
                  <img
                    src="/splash-icon.png"
                    alt=""
                    className="brand-logo"
                    aria-hidden="true"
                  />
                  <span className="brand-name">Polylect</span>
                </Link>

                {/* Burger: na mobile otwiera panel z linkami PUBLIC + (jeśli /admin) także ADMIN */}
                <button
                  className="burger"
                  aria-label="Otwórz menu"
                  aria-expanded={menuOpen}
                  aria-controls="topnav-drawer"
                  onClick={() => setMenuOpen((o) => !o)}
                >
                  <span className="burger-bar" />
                  <span className="burger-bar" />
                  <span className="burger-bar" />
                </button>
              </div>

              {/* Desktopowy pasek (mobile ukryty w CSS) */}
              <div id="topnav-menu" className="nav-menu">
                <div className="nav-links" role="navigation" aria-label="Główne linki">
                  <Link
                    to="/"
                    className={`nav-link ${isActive('/') ? 'active' : ''}`}
                    aria-current={isActive('/') ? 'page' : undefined}
                    onClick={closeMenu}
                  >
                    Home
                  </Link>
                  <Link
                    to="/admin"
                    className={`nav-link ${isActive('/admin') ? 'active' : ''}`}
                    aria-current={isActive('/admin') ? 'page' : undefined}
                    onClick={closeMenu}
                  >
                    Admin
                  </Link>
                </div>

                <div className="nav-right">
                  {/* TTL + ewentualny przycisk przedłużenia */}
                  {renderTtl()}
                  <div className="user-chip" title={email || undefined}>
                    <div className="avatar">{email?.charAt(0).toUpperCase()}</div>
                    <span className="user-email">{email}</span>
                  </div>
                  <button className="btn-ghost logout-btn" onClick={handleLogout}>
                    Wyloguj
                  </button>
                </div>
              </div>
            </div>
          </nav>

          {/* Offset pod belką */}
          <div className="top-nav-offset" aria-hidden />
          {/* Nakładka – kliknięcie zamyka */}
          {menuOpen && <div className="nav-overlay" onClick={closeMenu} aria-hidden />}
          {/* Drawer mobilny: public + (jeśli /admin) admin */}
          <div
            id="topnav-drawer"
            className={`nav-drawer ${menuOpen ? 'open' : ''}`}
            role="dialog"
            aria-label="Menu"
          >
            <div className="drawer-section">
              <Link to="/" className={`drawer-link ${isActive('/') ? 'active' : ''}`} onClick={closeMenu}>
                Home
              </Link>
              <Link to="/admin" className={`drawer-link ${isActive('/admin') ? 'active' : ''}`} onClick={closeMenu}>
                Admin
              </Link>
            </div>

            {inAdmin && (
              <div className="drawer-section">
                {adminLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`drawer-link ${isActive(link.to) ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            <div className="drawer-section">
              {/* TTL także w drawerze (mobile) */}
              {renderTtl()}
              <div className="user-chip drawer-chip" title={email || undefined}>
                <div className="avatar">{email?.charAt(0).toUpperCase()}</div>
                <span className="user-email">{email}</span>
              </div>
              <button className="btn btn-ghost drawer-btn" onClick={handleLogout}>
                Wyloguj
              </button>
            </div>
          </div>
        </>
      )}

      <Outlet />
    </>
  );
}
